import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";
import type { FormField } from "../../components/AdminDataModal";
import { useMessageModal } from "../../contexts/MessageModalContext";

interface Template {
  id: number;
  template_name: string;
}

interface Campaign {
  id: number;
  campaign_name: string;
  email_template_id: number;
  template_name?: string;
  audience: string;
  total_recipients?: number;
  delivery_status: string;
  status?: string;
  created_at?: string;
}

export default function SendCampaign() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();

  const [data, setData] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedRow, setSelectedRow] = useState<Campaign | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const [loading, setLoading] = useState(false);

  /* =========================
     FETCH CAMPAIGNS
  ========================= */

  const fetchCampaigns = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/settings/email-campaigns`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);
        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Campaigns Error:", error);
    }
  };

  /* =========================
     FETCH TEMPLATES
  ========================= */

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/settings/email-templates`, {
        credentials: "include",
      });

      const result = await res.json();

      if (result.success) {
        setTemplates(result.data);
      }
    } catch (error) {
      console.error("Fetch Templates Error:", error);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
  }, []);

  /* =========================
     FORM FIELDS
  ========================= */

  const campaignFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },

      { key: "campaign_name", label: "Campaign Name", colSpan: 2 },

      {
        key: "email_template_id",
        label: "Email Template",
        type: "select",
        options: templates.map((template) => ({
          value: template.id,
          label: template.template_name,
        })),
        colSpan: 2,
      },

      {
        key: "audience",
        label: "Audience",
        type: "select",
        options: [
          { label: "Subscribers", value: "subscribers" },
          { label: "Users", value: "users" },
          { label: "Public", value: "public" },
        ],
        colSpan: 2,
      },

      {
        key: "delivery_status",
        label: "Delivery Status",
        type: "select",
        options: [
          { label: "Draft", value: "draft" },
          { label: "Sent", value: "sent" },
          { label: "Failed", value: "failed" },
        ],
        colSpan: 2,
      },

      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    ],
    [templates],
  );

  const columns = [
    { key: "campaign_name", label: "Campaign Name" },
    { key: "template_name", label: "Template" },
    { key: "audience", label: "Audience" },
    { key: "delivery_status", label: "Delivery Status" },
    { key: "status", label: "Status" },
  ];

  /* =========================
     SAVE CAMPAIGN
  ========================= */

  const handleSaveCampaign = async (formData: any) => {
    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/settings/email-campaigns`
          : `${BASE_URL}/admin/settings/email-campaigns/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          campaign_name: formData.campaign_name,
          email_template_id: formData.email_template_id,
          audience: formData.audience,
          delivery_status: formData.delivery_status || "draft",
          status: formData.status || "active",
        }),
      });

      const result = await res.json();

      if (result.success) {
        fetchCampaigns();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Campaign saved successfully."
              : "Campaign updated successfully.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Save Campaign Error:", error);
    }
  };

  /* =========================
     SEND CAMPAIGN
  ========================= */

  const handleSendCampaign = async (formData: any) => {
    if (!formData.campaign_name || !formData.email_template_id) {
      showModal({
        type: "warning",
        title: "Missing Fields",
        message: "Please fill all required fields.",
        confirmText: "OK",
      });
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${BASE_URL}/admin/settings/send-campaign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          campaign_name: formData.campaign_name,
          template_id: formData.email_template_id,
          audience: formData.audience,
        }),
      });

      const result = await res.json();

      if (result.success) {
        fetchCampaigns();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message: "Campaign sent successfully.",
          confirmText: "OK",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Campaign failed.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Send Campaign Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Failed to send campaign.",
        confirmText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     DELETE
  ========================= */

  const handleDelete = (row: Campaign) => {
    showModal({
      type: "confirm",
      title: "Delete Campaign",
      message: `Are you sure you want to delete "${row.campaign_name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/admin/settings/email-campaigns/${row.id}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );

          const result = await res.json();

          if (result.success) {
            fetchCampaigns();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Campaign deleted successfully.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Campaign Error:", error);
        }
      },
    });
  };

  return (
    <div>
      <AdminListToolkit
        title="Email Campaigns"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow({
            campaign_name: "",
            email_template_id: "",
            audience: "subscribers",
            delivery_status: "draft",
            status: "active",
          });
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <div className="algorithm-grid">
            {filteredData.map((item: Campaign) => (
              <div
                key={item.id}
                className={`algorithm-card ${
                  selectedRow?.id === item.id ? "selected" : ""
                }`}
                onClick={() => setSelectedRow(item)}
              >
                <h3>{item.campaign_name}</h3>

                <p>
                  <strong>Template:</strong> {item.template_name || "-"}
                </p>

                <p>
                  <strong>Audience:</strong> {item.audience}
                </p>

                <p>
                  <strong>Delivery Status:</strong>{" "}
                  <span
                    className={
                      item.delivery_status === "sent"
                        ? "badge-success"
                        : item.delivery_status === "failed"
                          ? "badge-danger"
                          : "badge-warning"
                    }
                  >
                    {item.delivery_status?.charAt(0).toUpperCase() +
                      item.delivery_status?.slice(1)}
                  </span>
                </p>

                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={
                      item.status === "active"
                        ? "badge-success"
                        : "badge-danger"
                    }
                  >
                    {(item.status || "active").charAt(0).toUpperCase() +
                      item.status?.slice(1)}
                  </span>
                </p>

                <p>
                  <strong>Recipients:</strong> {item.total_recipients || 0}
                </p>
              </div>
            ))}
          </div>
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Email Campaign"
        fields={campaignFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={(formData) => setActiveRow(formData)}
        onFormChange={setActiveRow}
        hideDefaultSubmit
        footer={
          modalMode !== "view" ? (
            <>
              <button
                onClick={() => setModalOpen(false)}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>

              <button
                onClick={() => handleSaveCampaign(activeRow)}
                className="btn-secondary"
                disabled={loading}
              >
                Save
              </button>

              <button
                onClick={() => handleSendCampaign(activeRow)}
                className="btn-primary"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Email"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setModalOpen(false)}
              className="btn-secondary"
            >
              Close
            </button>
          )
        }
      />
    </div>
  );
}
